import * as Sentry from '@sentry/browser';
import { nanoid } from 'nanoid';
import { getCustomerDemo, getUrlJourney } from '@utils/url-helpers';
import { DEFAULT_DEMO_FOR_IMAGE_NAME, DEFAULT_JOURNEY_FOR_IMAGE_NAME, PRODUCT_TYPES } from '@utils/enums';

export function getShopifyIdFromBase64(base64Id) {
	if (typeof base64Id !== 'string') return;

	const decoded = atob(base64Id);
	const shopifyId = decoded.split('/').pop();
	return Number(shopifyId) || null;
}

export function getShopifyCart(root) {
	const cartContents = fetch(`${root}cart.js`)
		.then(res => res.json())
		.then(data => data)
		.catch(err => console.error('[getShopifyCart()]:', err.message));
	return cartContents;
}

/**
 * Generates the bundle so we can add items to Shopify cart and identify all of the products added together.
 * The prescription data is automatically added on the instantiation of array [items]; if they exist, all tops and lenses products
 * are added to the items array.
 *
 * @param {obj} 'object-literal'
 * @returns {obj} items
 */
export function generateBundle({
	_Frame,
	_customerJourney,
	_prescriptionType,
	_readerStrength,
	lens,
	sun_lens,
	sun_lens_url,
	tops,
	variant_id,
}) {
	const _bundle_key = nanoid();
	const _customerType = getCustomerDemo();
	const _submissionMethod = 'remind';

	const items = [
		{
			quantity: 1,
			id: variant_id,
			properties: {
				_bundle_key,
				_customerJourney,
				_customerType,
				_Frame,
				_prescriptionType,
				_submissionMethod,
				...(_customerJourney === 'SUN_LENS' && {
					_lensColor: sun_lens.option1,
					_lensImageUrl: sun_lens_url,
				}),
				...(_prescriptionType === 'Readers' && { _readerStrength }),
			},
		},
		lens.defaultLens && {
			quantity: 1,
			id: lens.defaultLens.id,
			properties: {
				_bundle_key,
				_customerJourney,
				_Frame,
				...(_prescriptionType === 'Non-Prescription' &&
					_customerJourney === 'BLUE_LIGHT' &&
					lens.defaultLens.sku === 'BLUE_LIGHT' && { _nonRxComp: true }),
			},
		},
	];

	tops.selected.length &&
		tops.selected.forEach(item => {
			items.push({
				quantity: 1,
				id: item.variantId,
				properties: {
					_bundle_key,
					_Frame,
				},
			});
		});

	lens.selected.length &&
		lens.selected.forEach(item => {
			items.push({
				quantity: 1,
				id: item.id,
				properties: {
					_bundle_key,
					_Frame,
				},
			});
		});

	return { items: items.filter(Boolean) };
}

export async function addToShopifyCart({ root, items, forwardToCart = true }) {
	try {
		const result = await fetch(`${root === '/' ? root : `${root}/`}cart/add.js`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(items),
		});

		if (result && result.ok) {
			if (forwardToCart) return forwardToCart && (window.location.href = '/cart');
			return result;
		}
	} catch (error) {
		console.error('[addToShopifyCart()]:', error.message);
		Sentry.captureException(error, error.message);
	}
}

/**
 * Removes "the" from Product Title
 *
 * @example "The Kirby" -> This function strips the "The" and returns the lowercase frame name "kirby"
 * @param {string} title
 * @return {string}
 */
export function getBaseFrameNameFromTitle({ title, makeLowerCase }) {
	const newTitle = title.split(' ')[1];

	if (makeLowerCase) return newTitle.toLowerCase();
	return newTitle;
}

/**
 * Shopify nests the relevant product variant data in a variants[] array; this pulls the relevant
 * information out of variants[] and normalizes it with the top-level product information
 */
export function normalizeCollectionData(collection) {
	return collection.map(({ body_html, handle, images, product_type, tags, title, variants }) => {
		const { available, id, price, product_id, sku } = variants[0];
		return {
			available,
			body_html,
			handle,
			id,
			images,
			product_id,
			product_type,
			sku,
			tags,
			title,
			price: Number(price),
		};
	});
}

export function formatColorTitle({ title, char }) {
	const array = title.split(' ');
	return array.length > 1 ? array.join(char) : title;
}

export function formatCurrency({ amount, locale = 'en', currency = 'USD', digits = 0 }) {
	return new Intl.NumberFormat(locale, {
		currency,
		style: 'currency',
		maximumFractionDigits: digits,
		minimumFractionDigits: digits,
	}).format(Number(amount));
}

export async function getCollectionMetafields({ handle, namespace, keys }) {
	const url = process.env.SHOPIFY_GRAPHQL_URL;
	const options = {
		method: 'POST',
		headers: {
			'X-Shopify-Storefront-Access-Token': process.env.SHOPIFY_STOREFRONT_API_TOKEN,
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			query: `query {
                        collection(handle: "${handle}") {
							${keys.map(
								key => `${key}: metafield(namespace: "${namespace}", key: "${key}") {
								${
									key.includes('image')
										? 'reference {\n' +
										  '        ... on MediaImage {\n' +
										  '          image {\n' +
										  '            originalSrc\n' +
										  '          }\n' +
										  '        }\n' +
										  '      }'
										: 'value'
								}
							}`
							)}

                        }
                    }`,
		}),
	};

	try {
		const response = await fetch(url, options);
		if (response.status !== 200) throw new Error(`HTTP Response ${response.status}: Collections Request Failed`);
		const data = await response.json();
		return data;
	} catch (error) {
		console.error(`HTTP ${error.status} ${error.statusText}: ${error.message}`);
		return error;
	}
}

export async function getCollectionMetafieldImage({ handle, namespace, key }) {
	const url = process.env.SHOPIFY_GRAPHQL_URL;
	const options = {
		method: 'POST',
		headers: {
			'X-Shopify-Storefront-Access-Token': process.env.SHOPIFY_STOREFRONT_API_TOKEN,
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			query: `query {
                        collection(handle: "${handle}") {
							${key}: metafield(namespace: "${namespace}", key: "${key}") {
								reference {
									... on MediaImage {
									  image {
										originalSrc
									  }
									}
								}
							}
                        }
                    }`,
		}),
	};

	try {
		const response = await fetch(url, options);
		if (response.status !== 200) throw new Error(`HTTP Response ${response.status}: Collections Request Failed`);
		const data = await response.json();
		return data;
	} catch (error) {
		console.error(`HTTP ${error.status} ${error.statusText}: ${error.message}`);
		return error;
	}
}

export async function getProductMetafields({ handle, namespace, key, getMetafieldFromVariants = true }) {
	const url = process.env.SHOPIFY_GRAPHQL_URL;
	const options = {
		method: 'POST',
		headers: {
			'X-Shopify-Storefront-Access-Token': process.env.SHOPIFY_STOREFRONT_API_TOKEN,
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			query: `query {
                        product(handle: "${handle}") {
							title
							${key}: metafield(namespace: "${namespace}", key: "${key}") {
								value
								type
							}
							${
								getMetafieldFromVariants &&
								`variants(first: 100){
								edges {
									node{
										title
										${key}: metafield(namespace: "${namespace}", key: "${key}") {
											value
											type
										}
									}
								}
							}`
							}

                        }
                    }`,
		}),
	};

	try {
		const response = await fetch(url, options);
		if (response.status !== 200) throw new Error(`HTTP Response ${response.status}: Collections Request Failed`);
		const data = await response.json();
		return data;
	} catch (error) {
		console.error(`HTTP ${error.status} ${error.statusText}: ${error.message}`);
		Sentry.captureException(error, `HTTP ${error.status} ${error.statusText}: ${error.message}`);
		return error;
	}
}

export async function getCollectionPreviewProducts(handle) {
	const url = process.env.SHOPIFY_GRAPHQL_URL;
	const options = {
		method: 'POST',
		headers: {
			'X-Shopify-Storefront-Access-Token': process.env.SHOPIFY_STOREFRONT_API_TOKEN,
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			query: `query {
						collectionByHandle(handle: "${handle}") {
							products(first: 50) {
								edges {
									node {
										id
										title
										handle
										variants(first: 20) {
											edges {
												node {
													id
													image {
														url
													}
													title
													sku
													availableForSale
													price
												}
											}
										}
										availableForSale
										featuredImage {
											url
										}
										productType
									}
								}
							}
						}
					}`,
		}),
	};

	try {
		const response = await fetch(url, options);
		if (response.status !== 200) throw new Error(`HTTP Response ${response.status}: Collections Request Failed`);
		const data = await response.json();
		return data;
	} catch (error) {
		console.error(`HTTP ${error.status} ${error.statusText}: ${error.message}`);
		return error;
	}
}

export const fetchRecommendations = async ({ productId, rootUrl }) => {
	try {
		const response = await fetch(`${rootUrl}recommendations/products.json?product_id=${productId}&limit=20`);

		if (response.ok) {
			const { products } = await response.json();
			if (products.length > 0) {
				const filteredProduct = products.filter(prod => prod.type !== 'LENS');
				return filteredProduct;
			}
		}

		if (response.status !== 200) {
			console.error(`Error while fetching recommendations: ${response.statusText}`);
		}
	} catch (e) {
		console.error(`Could not fetch the recommendation: ${e.message}`);
	}

	return [];
};

export const cleanGraphQLResponse = input => {
	if (!input) return null;
	const output = {};
	const isObject = obj => {
		return obj !== null && typeof obj === 'object' && !Array.isArray(obj);
	};

	Object.keys(input).forEach(key => {
		if (input[key] && input[key].edges) {
			output[key] = input[key].edges.map(edge => cleanGraphQLResponse(edge.node));
		} else if (isObject(input[key])) {
			output[key] = cleanGraphQLResponse(input[key]);
		} else if (key !== '__typename') {
			output[key] = input[key];
		}
	});

	return output;
};

/**
 * Generates URL strings for the 4th Carousel Image and the "About" section Lifestyle Image.
 * Destructure array to pull return values.
 *
 * @param {object} { journey: string, demo: string, title: string}
 * @return {array} [string, string]
 */
export const generateLifestyleUrls = title => {
	let carouselImageUrl;
	let lifestyleImageUrl;
	const demo = getCustomerDemo();
	const journey = getUrlJourney();
	const productTitle = getBaseFrameNameFromTitle({ title, makeLowerCase: true });
	const isAllGenderedNonRxFlow = demo === DEFAULT_DEMO_FOR_IMAGE_NAME && journey !== DEFAULT_JOURNEY_FOR_IMAGE_NAME;

	if (isAllGenderedNonRxFlow) {
		carouselImageUrl = `${window.filePath}carousel-image-4_${journey}_PDP_${demo}.png`;
		lifestyleImageUrl = `${window.filePath}alt-image_${journey}_PDP_${demo}.png`;
	} else {
		carouselImageUrl = `${window.filePath}carousel-image-4_${journey}_PDP_${productTitle}_${demo}.png`;
		lifestyleImageUrl = `${window.filePath}alt-image_${journey}_PDP_${productTitle}_${demo}.png`;
	}

	return [carouselImageUrl, lifestyleImageUrl];
};

export const generateVariantUrlStringsArray = ({ base, lens, length, title }) => {
	const baseColor = formatColorTitle({ title: base.toLowerCase(), char: '-' });
	const lensColor = formatColorTitle({
		title: lens.toLowerCase(),
		char: '-',
	});
	const baseFrameTitle = getBaseFrameNameFromTitle({ title, makeLowerCase: true });

	const addendum = `${baseColor}-${lensColor}_PDP_${baseFrameTitle}.png`;

	return Array.apply(null, Array(length)).map((__, i) => {
		const carouselUrl = `${window.filePath}carousel-image-${i + 1}-sun-${addendum}`;
		// if (i === 0) window.pageContext.buildFlow.lensImageUrl = carouselUrl;
		return carouselUrl;
	});
};

export const determineProductType = productType => {
	if (productType.includes(PRODUCT_TYPES.TOP_FRAME)) return PRODUCT_TYPES.TOP_FRAME;

	return productType;
};

export const getBaseProducts = async () => {
	const url = '/collections/all-base-frames/products.json';
	const response = await fetch(url);

	if (!response.ok) {
		console.error(`[STATUS ${response.status}]: ${response.statusText}`);
		return [];
	}
	const { products } = await response.json();
	return products;
};

export const getCollectionByHandle = async handle => {
	const url = `/collections/${handle}/products.json`;
	const response = await fetch(url);

	if (!response.ok) {
		console.error(`[STATUS ${response.status}]: ${response.statusText}`);
		return [];
	}
	const { products } = await response.json();
	return products;
};

export const constructBaseFrameVariantMap = array => {
	if (array.length === 0) return {};
	const map = {};

	array.forEach(frame => {
		const blackVariant = frame.variants.find(v => v.option1 === 'Black');
		const formattedTitle = getBaseFrameNameFromTitle({ title: frame.title, makeLowerCase: false });

		map[formattedTitle] = blackVariant;
	});

	return map;
};

export function calculateSubtotalFromLineItems(lineItems) {
	return lineItems.reduce(function totalReducer(accumulator, item) {
		return accumulator + Number(item.price);
	}, 0);
}

export const getProductType = (product, isLineItem = false) => {
	return (isLineItem ? product?.type ?? product.product_type : product?.type ?? product.productType) ?? 'MISC';
};
export const addItemToCart = async ({ variantId, quantity, bundleKey, frame, product }) => {
	const parsedProduct = typeof product === 'string' ? JSON.parse(product) : product;
	const formData = {
		items: [
			{
				id: variantId,
				quantity,
				...(bundleKey && { properties: { _bundle_key: bundleKey.toString() } }),
			},
		],
	};
	if (frame) {
		formData.items[0].properties._Frame = frame;
	}

	return fetch('/cart/add.js', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(formData),
	})
		.then(response => response.json())
		.then(data => {
			pushToDataLayer({ eventType: 'addToCart', product: parsedProduct, currentVariantId: variantId });
			return data;
		})
		.catch(error => {
			console.error('Error:', error);
		});
};

export const deleteItemFromCart = (itemKey, qty) => {
	// use line item key instead of variant id to account for duplicates
	const data = {
		quantity: qty,
		id: itemKey.toString(),
	};

	return fetch('/cart/change.js', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(data),
	})
		.then(response => response)
		.catch(error => {
			console.error('Error:', error);
		});
};

export const changeCart = async data => {
	return await fetch('/cart/change.js', {
		method: 'POST',
		credentials: 'same-origin',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(data),
	})
		.then(res => {
			if (res.status !== 200) {
				console.error('Request returned an error', res);
			}
		})
		.catch(error => {
			console.error('Request failed', error);
		});
};

export const deleteBundle = async data => {
	return await fetch('/cart/update.js', {
		method: 'POST',
		credentials: 'same-origin',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(data),
	})
		.then(res => {
			if (res.status !== 200) {
				console.error('Request returned an error', res);
			}
		})
		.catch(error => {
			console.error('Request failed', error);
		});
};

export const updateSubmissionMethodForBase = async data => {
	try {
		const response = await fetch('/cart/change.js', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		});
		return response.ok;
	} catch (error) {
		console.error(`Could not update prescription submission method: ${error}`);
		Sentry.captureException(error, error.message);
		return false;
	}
};

function pushToDataLayer({ eventType = 'productView', product, currentVariantId }) {
	const lineItem = !product.variants;
	const variant = lineItem ? product : product.variants.find(v => v.id === Number(currentVariantId));
	const productOrVariantData = {
		name: lineItem ? product.name.split(' - ')[0] : product.title,
		id: variant.id,
		price: parseFloat((product.price / 100).toFixed(2)),
		category: (lineItem ? product.product_type : product.type) ?? 'MISC',
		variant: variant.option1,
		image_url: variant.featured_image?.src,
	};

	window.dataLayer = window.dataLayer || [];
	dataLayer.push({
		event: `enhanceEcom ${eventType}`,
		ecommerce: {
			currencyCode: theme.currency,
			...(eventType === 'productView' && {
				detail: {
					products: [
						{
							...productOrVariantData,
						},
					],
				},
			}),
			...(eventType === 'addToCart' && {
				add: {
					products: [
						{
							quantity: 1,
							...productOrVariantData,
						},
					],
				},
			}),
		},
	});
}
export const sanitizeCmsImage = possibleObject => {
	return typeof possibleObject === 'object' ? possibleObject?.src ?? '' : possibleObject;
};

export const checkForLineItem = (cartItems, variantId) => {
	try {
		if (cartItems.some(item => item.id === variantId)) {
			return true;
		}
	} catch (error) {
		console.warn(`${error}`);
	}
	return false;
};

export const checkForNonRxDiscount = (handle, nonRxGroup) => {
	return handle === 'blue-light-filtering' && nonRxGroup;
};
